import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeletePackageFeed } from '@/hooks/usePackageFeeds'
import type { PackageFeed } from '@/types'

interface DeletePackageFeedDialogProps {
  projectId: string
  feed: PackageFeed
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeletePackageFeedDialog({ projectId, feed, open, onOpenChange }: DeletePackageFeedDialogProps) {
  const deleteFeed = useDeletePackageFeed(projectId)

  const handleDelete = async () => {
    try {
      await deleteFeed.mutateAsync(feed.id)
      toast.success('Package feed removed.')
      onOpenChange(false)
    } catch {
      toast.error('Failed to remove package feed.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete package feed?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the package feed "<span className="font-bold">{feed.name}</span>"? This
          action cannot be undone, and workflow sandboxes in this project will no longer have access to this feed.
        </DialogDescription>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteFeed.isPending}
            data-testid="feed-delete-confirm"
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            <Trash2 className="h-5 w-5 mr-1" />
            {deleteFeed.isPending ? 'Deleting…' : 'Delete feed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
